/**
 * cajaMulticaja.ts — Estado multicaja en mocks (sesiones por punto de venta).
 *
 * QUÉ HACE:
 * - Catálogo de puntos (`caja_puntos_catalogo`)
 * - Mapa de sesiones abiertas por `puntoCajaId` (`caja_sesiones_activas`)
 * - Migra `caja_sesion_activa` legado al mapa
 * - Espeja la sesión del punto seleccionado en `caja_sesion_activa` para código que aún la lee
 *
 * QUÉ NO DEBE HACER: Llamar a React ni a la API real.
 */
import type { PuntoCajaListItem } from "@/features/caja/domain/puntoCaja.types";
import {
  txRead,
  txRemove,
  txWrite,
} from "@/lib/persistence/transactionalStore";

export const CAJA_PUNTOS_KEY = "caja_puntos_catalogo";
export const CAJA_SESIONES_MAP_KEY = "caja_sesiones_activas";
export const LS_PUNTO_CAJA = "altenot_punto_caja_id";

export function ensurePuntosDemo(): PuntoCajaListItem[] {
  let pts = txRead<PuntoCajaListItem[] | null>(CAJA_PUNTOS_KEY, null);
  if (!Array.isArray(pts) || pts.length === 0) {
    pts = [{ id: "pc1", nombre: "Caja principal", orden: 0, activa: true }];
    txWrite(CAJA_PUNTOS_KEY, pts);
  }
  return pts;
}

export function getSesionesMapRaw(): Record<string, Record<string, unknown>> {
  let map = txRead<Record<string, Record<string, unknown>> | null>(
    CAJA_SESIONES_MAP_KEY,
    null,
  );
  if (!map || typeof map !== "object" || Array.isArray(map)) {
    map = {};
  }
  if (Object.keys(map).length === 0) {
    const legacy = txRead<Record<string, unknown> | null>(
      "caja_sesion_activa",
      null,
    );
    if (legacy) {
      const pid = (legacy.puntoCajaId as string) || "pc1";
      map = { [pid]: { ...legacy, puntoCajaId: pid } };
      txWrite(CAJA_SESIONES_MAP_KEY, map);
    }
  }
  return map;
}

export function persistSesionesMap(
  map: Record<string, Record<string, unknown>>,
) {
  txWrite(CAJA_SESIONES_MAP_KEY, map);
}

export function nombrePuntoDemo(pid: string): string {
  const punto = ensurePuntosDemo().find((p: { id: string }) => p.id === pid) as
    | { nombre?: string; alias?: string }
    | undefined;
  if (!punto) return pid;
  return punto.alias?.trim()
    ? `${punto.nombre} — ${punto.alias.trim()}`
    : (punto.nombre ?? pid);
}

/** Mantiene compatibilidad con handlers que aún leen `caja_sesion_activa`. */
export function mirrorSesionLegacy(
  sesion: Record<string, unknown> | null,
  puntoId: string | null,
) {
  if (sesion && puntoId) {
    txWrite("caja_sesion_activa", { ...sesion, puntoCajaId: puntoId });
  } else {
    txRemove("caja_sesion_activa");
  }
}

export function resolvePuntoCajaIdFromReq(
  req: { url?: { searchParams?: { get?: (key: string) => string | null } } },
  body?: Record<string, unknown>,
): string {
  const fromUrl = req.url?.searchParams?.get?.("puntoCajaId");
  const fromBody = body?.puntoCajaId;
  if (fromBody != null && String(fromBody).trim() !== "") {
    return String(fromBody).trim();
  }
  if (fromUrl != null && String(fromUrl).trim() !== "") {
    return String(fromUrl).trim();
  }
  try {
    const ls =
      typeof localStorage !== "undefined"
        ? localStorage.getItem(LS_PUNTO_CAJA)
        : null;
    if (ls) return ls;
  } catch {
    /* ignore */
  }
  return ensurePuntosDemo()[0]?.id || "pc1";
}

export function getSesionActivaParaPunto(
  puntoId: string,
): Record<string, unknown> | null {
  const map = getSesionesMapRaw();
  return map[puntoId] || null;
}

/** Tras cerrar o cambiar punto: espejar la sesión del punto actual en LS. */
export function resyncLegacyMirrorForLsPunto() {
  const pid = resolvePuntoCajaIdFromReq({
    url: { searchParams: new URLSearchParams() },
  });
  const sesion = getSesionActivaParaPunto(pid);
  mirrorSesionLegacy(sesion, sesion ? pid : null);
}
